import DeleteIconButton from '../components/DeleteIconButton';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import { Divider, Grid, ListItem, ListItemText, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { GridCheckIcon, GridCloseIcon, gridClasses } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory, useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { DataAPIContext } from '../api';
import { DELETE_BATCH, UPDATE_BATCH, BULK_ACTION } from '../api/dataProvider';
import { isBackgroundTaskResponse, notifyBackgroundTask } from '../bg_tasks/batchResponseHelper';
import { BackgroundTaskContext } from '../bg_tasks/BackgroundTaskProvider';
import ConfirmationDialog from '../components/ConfirmationDialog';
import EntityExportDialog from '../components/EntityExportDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import useGridExport from '../components/useGridExport';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import ItineraryListToolbar from './ItineraryListToolbar';
import ItineraryPreview from './ItineraryPreview';
import { ConfirmationDialogText } from './utils';
import EditDisplaySettingsDialog from './EditDisplaySettingsDialog';

const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: 0,
        margin: theme.spacing(2),
        marginTop: 0,
        overflow: 'auto',
        display: 'flex',
    },
    gridRoot: {},
    recordList: {
        marginTop: theme.spacing(2),
        height: `calc(${theme.cp.contentHeight} - 6rem) !important`,
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
    sortButton: {
        minWidth: theme.spacing(4),
        minHeight: theme.spacing(4),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        padding: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    progress: {
        marginBottom: theme.spacing(1),
    },
}));

const ItineraryList = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const classes = useStyles();
    const resource = 'itinerary';
    const urlParams = new URLSearchParams(window.location.search);
    const [query, setQuery] = React.useState(urlParams.get('query') || null);
    const [syncActive, setSyncActive] = React.useState(false);
    const [selectedIds, setSelectedIds] = React.useState([]);

    // The filters the grid is ACTUALLY using: its interactive column filters,
    // which RecordList owns and publishes. Every export goes through these so
    // the file holds the rows on screen (the CP-8258 filter contract).
    const [gridFilters, setGridFilters] = React.useState([]);

    // XML/JSON entity export of the selected itineraries. The export menu
    // has one item per format, so the dialog opens on the one that was
    // clicked; the bulk-actions entry passes nothing and keeps the last choice.
    const [itineraryExportOpen, setItineraryExportOpen] = React.useState(false);
    const [itineraryExportFormat, setItineraryExportFormat] = React.useState(null);
    const openItineraryExport = React.useCallback((format) => {
        setItineraryExportFormat(typeof format === 'string' ? format : null);
        setItineraryExportOpen(true);
    }, []);
    const { exportDocument: exportItineraryDocument, exporting: itineraryExporting } = useGridExport({
        resource: 'itinerary',
        apiFilters: gridFilters,
        query,
        selectedIds,
        isAllSelected: false,
    });
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [newRowOrder, setNewRowOrder] = React.useState(null);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const bgCtx = React.useContext(BackgroundTaskContext);
    const isSmallScreen = !useIsWidthUp('sm');
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const [idsToUnpublish, setIdsToUnpublish] = React.useState(null);
    const [idsToPublish, setIdsToPublish] = React.useState(null);
    const [idsChangeDisplaySettings, setIdsChangeDisplaySettings] = React.useState(null);
    const [displaySettings, setDisplaySettings] = React.useState(false);
    const [editDisplaySettingsMenuOpen, setEditDisplaySettingsMenuOpen] = React.useState(false);
    const { hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const history = useHistory();
    const routerParams = useParams();
    const [activeItineraryId, setActiveItineraryId] = React.useState(routerParams.id);
    const cols = isSmallScreen ? (activeItineraryId ? 0 : 12) : activeItineraryId ? 8 : 12;

    React.useEffect(() => {
        if (!routerParams.id) {
            setActiveItineraryId(null);
        }
    }, [props, routerParams.id]);

    const onQueryChange = (query) => {
        setQuery(query);
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('query', query);
        props.setLastURLParams(searchParams)
        history.push(`${window.location.pathname}?${searchParams.toString()}`);
    };

    const onSelectionModelChange = (ids, selection) => {
        setSelectedIds(selection);
        if (selection.length === 1) {
            history.push(`/explore/itineraries/${selection[0]}/preview`);
            setActiveItineraryId(selection[0]);
        } else {
            history.push(`/explore/itineraries`);
            setActiveItineraryId(null);
        }
    };
    const doDeleteItineraryList = (idlist) => {
        setSyncActive(true);
        dataAPI(DELETE_BATCH, resource, { data: { list: idlist } })
            .then((res) => {
                if (isBackgroundTaskResponse(res)) {
                    notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
                } else {
                    enqueueSnackbar(idlist.length > 1 ? 'Itineraries deleted' : 'Itinerary deleted', { variant: 'default' });
                }
                setLastUpdate(new Date().toString());
                setSelectedIds([]);
                setSyncActive(false);
                history.push(`/explore/itineraries`);
            })
            .catch((err) => {
                enqueueSnackbar('Error deleting itinerary', { variant: 'error' });
            })
            .finally(() => {
                setSelectedIds([]);
                setSyncActive(false);
            });
    };

    const doUnpublishItineraryList = (idlist) => {
        setSyncActive(true);
        dataAPI(BULK_ACTION, resource, { action: 'unpublish', data: { list: idlist} })
            .then((res) => {
                if (isBackgroundTaskResponse(res)) {
                    notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
                } else {
                    enqueueSnackbar('Itineraries unpublished', { variant: 'default' });
                }
                setLastUpdate(new Date().toString());
                setSelectedIds([]);
                setSyncActive(false);
            })
            .catch((err) => {
                enqueueSnackbar('Error unpublishing itinerary', { variant: 'error' });
            });
    };

    const doPublishItineraryList = (idlist) => {
        setSyncActive(true);
        dataAPI(BULK_ACTION, resource, { action: 'publish', data: { list: idlist} })
            .then((res) => {
                if (isBackgroundTaskResponse(res)) {
                    notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
                } else {
                    enqueueSnackbar('Itineraries published', { variant: 'default' });
                }
                setLastUpdate(new Date().toString());
                setSelectedIds([]);
                setSyncActive(false);
            })
            .catch((err) => {
                enqueueSnackbar('Error publishing itinerary', { variant: 'error' });
            });
    };

    const doChangeChangeDisplaySettingsItineraryList = (idlist) => {
        setSyncActive(true);
        dataAPI(BULK_ACTION, resource, { action: 'change_display_settings', data: { list: idlist} })
            .then((res) => {
                console.log('change_display_settings res', res);
                setLastUpdate(new Date().toString());
                enqueueSnackbar('Itineraries updated', { variant: 'default' });
                setSelectedIds([]);
                setSyncActive(false);
            })
            .catch((err) => {
                enqueueSnackbar('Error updating itinerary', { variant: 'error' });
            });

    };

    const onDeleteItineraryList = (idlist) => {
        setIdsToDelete(idlist);
        setConfirmationDialogAction(idlist.length > 1 ? 'delete_itineraries' : 'delete_itinerary');
    };

    const onUnpublishItineraryList = (idlist) => {
        setIdsToUnpublish(idlist);
        setConfirmationDialogAction('unpublish_itineraries');
    };

    const onPublishItineraryList = (idlist) => {
        setIdsToPublish(idlist);
        setConfirmationDialogAction('publish_itineraries');
    };

    const onChangeDisplaySettingsItineraryList = (idlist) => {
        setIdsChangeDisplaySettings(idlist);
        setEditDisplaySettingsMenuOpen(true);
    };

    const onSaveChangeDisplaySettingsItineraryList = (idlist) => {
        // setEditDisplaySettingsMenuOpen(false);
        setConfirmationDialogAction('change_display_settings_itineraries');
    };

    const onItineraryDelete = (f) => {
        setLastUpdate(new Date().toString());
    };

    const onItineraryUnpublish = (f) => {
        setLastUpdate(new Date().toString());
    };

    const onRowClick = (p) => {
        setActiveItineraryId(p.row.id);
        history.push(`/explore/itineraries/${p.row.id}/preview`);
    };

    const onDetailsClose = (ev) => {
        setActiveItineraryId(null);
        history.push(`/explore/itineraries`);
    };

    const onItineraryChange = (fn) => {
        setLastUpdate(new Date().toString());
    };

    const onRowOrderUpdate = async (upd) => {
        // Drag & drop saves immediately — leaving the change pending until a separate
        // 'Save new order' click loses it to the next background refetch.
        if (upd.direction === 'drag' || Object.keys(upd).length === 2) {
            await onSaveNewOrder(false, { ...newRowOrder, ...upd });
            return;
        }
        const newOrder = { ...newRowOrder, ...upd };
        setNewRowOrder(newOrder);
    };

    const onSaveNewOrder = async (reset_order, instantSaveNewOrder) => {
        let rowOrder = instantSaveNewOrder || newRowOrder;
        if (reset_order) {
            setNewRowOrder(null);
            setLastUpdate(new Date().toString());
            enqueueSnackbar('New order canceled', { variant: 'default' });
        } else {
            setSyncActive(true);
            await dataAPI(UPDATE_BATCH, resource, { data: rowOrder, urlSuffix: 'reorder/' })
                .then((res) => {
                    console.log('reorder res', res);
                    if (res.statusCode < 400) {
                        setLastUpdate(new Date().toString());
                        setNewRowOrder(null);
                        enqueueSnackbar('New order saved', { variant: 'success' });
                    } else {
                        setLastUpdate(new Date().toString());
                        enqueueSnackbar('Error saving new filter order', { variant: 'error' });
                    }
                })
                .catch((err) => {
                    setLastUpdate(new Date().toString());
                    enqueueSnackbar('Error saving new filter order', { variant: 'error' });
                })
                .finally(() => {
                    setSyncActive(false);
                });
        }
    };

    const listItemRenderer = (row, idx, rows, gridApi) => {
        const dayCount = row.length;
        const regions = row.regions.map((r) => r.name).join(', ');
        const tripTypes = row.trip_types.map((r) => r.name).join(', ');

        let checkDurationDisplay = row.custom_day_titles && row.duration_display && row.duration_display !== '' && row.length > 0 ? row.duration_display : null;

        return (
            <React.Fragment>
                <ListItem alignItems="flex-start" className={classes.listItem} onClick={(ev) => gridApi.onRowClick({ row, idx }, ev)}>
                    <ListItemText
                        primary={row.name}
                        secondary={
                            <Grid container direction="row" spacing={1}>
                                {!checkDurationDisplay ? (
                                    <Grid item xs={6}>
                                        {dayCount} day{dayCount > 1 ? 's' : ''}
                                    </Grid>
                                ) : (
                                    <Grid item xs={6}>
                                        {checkDurationDisplay}
                                    </Grid>
                                )}
                                <Grid item xs={6} container justifyContent="flex-end">
                                    {row.published ? 'Published' : 'Draft'}
                                </Grid>
                                <Grid item xs={12}>
                                    {regions}
                                </Grid>
                                <Grid item xs={12}>
                                    {tripTypes}
                                </Grid>
                            </Grid>
                        }
                    />
                </ListItem>
                {idx < rows.length - 1 ? <Divider /> : null}
            </React.Fragment>
        );
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_itinerary':
            case 'delete_itineraries':
                doDeleteItineraryList(idsToDelete);
                break;
            case 'unpublish_itineraries':
                doUnpublishItineraryList(idsToUnpublish);
                break;
            case 'publish_itineraries':
                doPublishItineraryList(idsToPublish);
                break;
            case 'change_display_settings_itineraries':
                doChangeChangeDisplaySettingsItineraryList(idsChangeDisplaySettings);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const handleDelete = (id) => {
        setIdsToDelete([id]);
        setConfirmationDialogAction('delete_itinerary');
    };

    return (
        <div className={classes.root}>
            <PageMeta title="Itineraries" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive} />
            <Grid container direction="row" spacing={2}>
                {isSmallScreen && activeItineraryId ? null : (
                    <Grid item xs={cols} container direction="row" style={{ height: '100%' }}>
                        <Grid item xs={12}>
                            <PageHeading sx={{ mt: 2, mb: 1.5 }}>Itineraries</PageHeading>
                            <ItineraryListToolbar
                                resource={resource}
                                selectedIds={selectedIds}
                                apiFilters={gridFilters}
                                query={query}
                                onQueryChange={onQueryChange}
                                onDeleteItinerary={onDeleteItineraryList}
                                onUnpublishItinerary={onUnpublishItineraryList}
                                onPublishItinerary={onPublishItineraryList}
                                onChangeDisplaySettings={onChangeDisplaySettingsItineraryList}
                                onResetSelection={() => setSelectedIds([])}
                                onDocumentExport={openItineraryExport}
                                newRowOrder={newRowOrder}
                                onSaveNewOrder={onSaveNewOrder}
                                isSmallScreen={isSmallScreen}
                                selectedCount={selectedIds.length}
                            />
                            <EntityExportDialog
                                open={itineraryExportOpen}
                                onClose={() => setItineraryExportOpen(false)}
                                entity="itinerary"
                                initialFormat={itineraryExportFormat}
                                exportDocument={exportItineraryDocument}
                                exporting={itineraryExporting}
                                selectionLabel={selectedIds.length
                                    ? `${selectedIds.length} selected itineraries`
                                    : 'all itineraries matching the current search'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <RecordList
                                className={classes.recordList}
                                resource={resource}
                                flavor="pro"
                                // Golden Tours re-orders itineraries by finding a row with browser
                                // Ctrl+F, then dragging it. Virtualized rows aren't in the DOM, so
                                // Ctrl+F can't find or scroll to them (CP dashboard support report).
                                disableVirtualization
                                rowReordering={hasPerm('itinerary', 'write')}
                                rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
                                disableSelectionOnClick={true}
                                selectionModel={selectedIds}
                                onSelectionModelChange={onSelectionModelChange}
                                sortModel={[{ field: 'sequence', sort: 'asc' }]}
                                // sortingMode="client"
                                onRowClick={onRowClick}
                                deps={lastUpdate}
                                query={query}
                                onEffectiveFiltersChange={setGridFilters}
                                onRowOrderUpdate={onRowOrderUpdate}
                                listItemRenderer={listItemRenderer}
                                isSmallScreen={isSmallScreen}
                                limitHeightForMobile={'calc(100vh - 12rem)'}
                                columns={[
                                    ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                                    { field: 'sequence', width: 110, filterable: false, sortable: false, minWidth: 90, headerName: 'Sequence', type: 'reorder' },
                                    { field: 'name', headerName: 'Name', sortable: true, flex: 1 },
                                    {
                                        field: 'length',
                                        headerName: 'Length',
                                        filterable: false,
                                        sortable: true,
                                        width: 200,
                                        valueGetter: (p) => (p.row.duration_display ? p.row.duration_display : p.row.length),
                                        valueFormatter: (p) => `${p.value} ${!(typeof p.value === 'string') ? (p.value === 1 ? 'day' : 'days') : ''}`,
                                    },
                                    { field: 'regions', headerName: 'Regions', sortable: false, flex: 0.8, valueGetter: (p) => p.row.regions.map((r) => r.name).join(', ') },
                                    { field: 'trip_types', headerName: 'Trip Types', sortable: false, flex: 0.8, valueGetter: (p) => p.row.trip_types.map((r) => r.name).join(', ') },
                                    {
                                        field: 'published',
                                        headerName: 'Published',
                                        sortable: false,
                                        type: 'boolean',
                                        width: 120,
                                        valueFormatter: (p) => (p.value ? 'Published' : 'Draft'),
                                        // Same check / cross the default boolean cell draws, but with an MUI
                                        // tooltip instead of the SVG's native "yes"/"no" title (CP-5558).
                                        renderCell: (p) => {
                                            const BooleanIcon = p.value ? GridCheckIcon : GridCloseIcon;
                                            return (
                                                <Tooltip title={p.value ? 'Published' : 'Draft'}>
                                                    <BooleanIcon fontSize="small" className={gridClasses.booleanCell} data-value={Boolean(p.value)} />
                                                </Tooltip>
                                            );
                                        },
                                    },
                                    { field: 'atag', headerName: 'Access tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', ') },
                                    {
                                        field: 'buttons',
                                        headerName: 'Action',
                                        sortable: false,
                                        renderCell: (p) => {
                                            return (
                                                <>
                                                <Tooltip title="Edit">
                                                <Link to={`/explore/itineraries/${p.id}`} onClick={(e) => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                                        <EditIcon color="primary" fontSize="small" />
                                                </Link>
                                                </Tooltip>
                                                <span style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                                    <DeleteIconButton
                                                        perm="itinerary.delete"
                                                        size="medium"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(p.id);
                                                        }}
                                                    />
                                                </span>
                                             </>
                                            );
                                        },
                                    },
                                ]}
                            />
                        </Grid>
                    </Grid>
                )}
                {activeItineraryId ? (
                    <Grid item xs={12 - cols}>
                        <ItineraryPreview
                            id={activeItineraryId}
                            resource={resource}
                            // data={activeItinerary}
                            onClose={onDetailsClose}
                            onChange={onItineraryChange}
                            onDelete={onItineraryDelete}
                            onUnpublish={onItineraryUnpublish}
                            syncActive={syncActive}
                            setSyncActive={setSyncActive}
                            isSmallScreen={isSmallScreen}
                        />
                    </Grid>
                ) : null}
                <EditDisplaySettingsDialog
                  open={editDisplaySettingsMenuOpen && selectedIds.length > 1}
                  onClose={() => setEditDisplaySettingsMenuOpen(false)}
                  onSave={() => onSaveChangeDisplaySettingsItineraryList()}
                  displaySettings={displaySettings}
                  setDisplaySettings={setDisplaySettings}
                  selectedCount={selectedIds.length}
                />
            </Grid>
        </div>
    );
};

export default ItineraryList;
